import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { deskApi } from '@/api';
import { filesApi } from '@/api/files.api';
import type { RootState } from '@/store';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type {
  DeskAttachment,
  DeskCannedResponse,
  DeskStatus,
  DeskTicketDetail,
  DeskTicketTimelineItem,
} from '@/types/desk';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function DeskTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const userId = useSelector((s: RootState) => s.auth.user?.userId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [detail, setDetail] = useState<DeskTicketDetail | null>(null);
  const [statuses, setStatuses] = useState<DeskStatus[]>([]);
  const [attachments, setAttachments] = useState<DeskAttachment[]>([]);
  const [cannedResponses, setCannedResponses] = useState<DeskCannedResponse[]>([]);
  const [comment, setComment] = useState('');
  const [internal, setInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [csatRating, setCsatRating] = useState(5);
  const [csatFeedback, setCsatFeedback] = useState('');
  const [csatSaving, setCsatSaving] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    const id = Number(ticketId);
    try {
      const [d, s, a, c] = await Promise.all([
        deskApi.getTicket(id, userId),
        deskApi.listStatuses(),
        deskApi.listAttachments(id).catch(() => [] as DeskAttachment[]),
        deskApi.listCannedResponses().catch(() => [] as DeskCannedResponse[]),
      ]);
      setDetail(d);
      setStatuses(s);
      setAttachments(a);
      setCannedResponses(c);
    } catch (err) {
      showApiError(err, 'Failed to load ticket');
    }
  }, [ticketId, userId]);

  useEffect(() => { load(); }, [load]);

  const addComment = async () => {
    if (!ticketId || !comment.trim()) return;
    setSaving(true);
    try {
      await deskApi.addComment(Number(ticketId), comment, internal);
      setComment('');
      notify.success('Added to timeline');
      load();
    } catch (err) {
      showApiError(err, 'Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (statusId: number) => {
    if (!ticketId) return;
    try {
      await deskApi.transitionTicket(Number(ticketId), statusId);
      notify.success('Status updated');
      load();
    } catch (err) {
      showApiError(err, 'Transition failed');
    }
  };

  const uploadAttachment = async (file: File) => {
    if (!ticketId) return;
    setUploading(true);
    try {
      const uploaded = await filesApi.upload(file, 'desk');
      await deskApi.addAttachment(Number(ticketId), {
        fileUrl: uploaded.url,
        fileName: uploaded.fileName || file.name,
        mimeType: file.type,
        fileSize: file.size,
      });
      notify.success('Attachment added');
      setAttachments(await deskApi.listAttachments(Number(ticketId)));
    } catch (err) {
      showApiError(err, 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const applyCanned = (body: string) => {
    setComment((prev) => (prev ? `${prev}\n${body}` : body));
  };

  const recordCsat = async () => {
    if (!ticketId) return;
    setCsatSaving(true);
    try {
      await deskApi.submitCsat(Number(ticketId), csatRating, csatFeedback.trim() || undefined);
      notify.success('CSAT recorded');
      setCsatFeedback('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to record CSAT');
    } finally {
      setCsatSaving(false);
    }
  };

  if (!detail) {
    return <div className="p-6 text-slate-500">Loading ticket…</div>;
  }

  const t = detail.ticket;

  return (
    <div>
      {detail.activeViewers.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Also viewing: {detail.activeViewers.join(', ')}
        </div>
      )}

      <PageHeader
        title={t.subject}
        description={`${t.ticketNumber} · ${t.source}`}
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Desk', to: '/desk' },
          { label: 'Tickets', to: '/desk/tickets' },
          { label: t.ticketNumber },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1 panel space-y-3">
          <h3 className="font-semibold text-slate-800">Details</h3>
          <p className="text-sm text-slate-600">{t.description || 'No description'}</p>
          {t.customerId && (
            <p className="text-sm">
              Customer:{' '}
              <Link to={`/desk/support-360/${t.customerId}`} className="text-primary-600 hover:underline">
                #{t.customerId}
              </Link>
            </p>
          )}
          {t.dueAt && <p className="text-sm text-slate-500">Due: {new Date(t.dueAt).toLocaleString()}</p>}
          <div className="pt-2">
            <p className="text-xs text-slate-500 mb-1">Change status</p>
            <div className="flex flex-wrap gap-1">
              {statuses.map((s) => (
                <Button key={s.id} variant="secondary" onClick={() => changeStatus(s.id)}>
                  {s.name}
                </Button>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm">Attachments</h4>
              <Button
                variant="secondary"
                loading={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadAttachment(file);
                  e.target.value = '';
                }}
              />
            </div>
            {attachments.length === 0 ? (
              <p className="text-sm text-slate-500">No attachments</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {attachments.map((a) => (
                  <li key={a.id}>
                    <a href={a.fileUrl} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                      {a.fileName || a.fileUrl}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t pt-3">
            <h4 className="font-semibold text-sm mb-2">Customer satisfaction (CSAT)</h4>
            <div className="flex items-center gap-2 mb-2">
              <Select
                label="Rating"
                options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} / 5` }))}
                value={String(csatRating)}
                onChange={(v) => setCsatRating(Number(v))}
                className="flex-1"
              />
            </div>
            <Input
              label="Feedback (optional)"
              value={csatFeedback}
              onChange={(e) => setCsatFeedback(e.target.value)}
            />
            <Button className="mt-2" variant="secondary" loading={csatSaving} onClick={recordCsat}>
              Record CSAT
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 panel">
          <h3 className="font-semibold text-slate-800 mb-3">Ticket timeline</h3>
          <ul className="space-y-3 max-h-[28rem] overflow-y-auto mb-4">
            {detail.timeline.length === 0 && (
              <li className="text-sm text-slate-500">No timeline items yet</li>
            )}
            {detail.timeline.map((item, idx) => (
              <TimelineRow key={`${item.itemType}-${idx}`} item={item} />
            ))}
          </ul>
          <div className="border-t pt-4 space-y-2">
            {cannedResponses.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {cannedResponses.map((c) => (
                  <Button key={c.id} variant="secondary" onClick={() => applyCanned(c.bodyText)}>
                    {c.title}
                  </Button>
                ))}
              </div>
            )}
            <Input label="Reply / note" value={comment} onChange={(e) => setComment(e.target.value)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              Internal note
            </label>
            <Button onClick={addComment} loading={saving}>Add to timeline</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ item }: { item: DeskTicketTimelineItem }) {
  return (
    <li className="border-l-2 border-primary-200 pl-3">
      <p className="text-xs text-slate-500">
        {item.itemType}
        {item.channel ? ` · ${item.channel}` : ''}
        {item.internalNote ? ' · internal' : ''}
      </p>
      <p className="font-medium text-sm">{item.title}</p>
      {item.body && <p className="text-sm text-slate-600">{item.body}</p>}
      {item.occurredAt && (
        <p className="text-xs text-slate-400">{new Date(item.occurredAt).toLocaleString()}</p>
      )}
    </li>
  );
}
